import { Link } from "@tanstack/react-router"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { cn } from "design-system/utils"
import { ensure, formatNumber, formatPercent, isNil } from "lopermut"
import { usePredefinedSegmentTranslations } from "modules/segments/hooks/usePredefinedSegmentTranslations"
import { useSegmentGroupShopifyMetricsQuery } from "modules/segments/hooks/useSegmentGroupShopifyMetricsQuery"
import { RFM_LAYOUT } from "modules/segments/lib/rfmLayout"
import { Loading } from "modules/shared/components/Loading"
import { Fragment } from "react"
import { useTranslation } from "react-i18next"
import {
  SegmentGroupResponseDataSchema,
  SegmentGroupShopifyMetricsResponseDataSchema,
  SegmentResponseDataSchema,
} from "schema"
import z from "zod"

const SCORES = [1, 2, 3, 4, 5]
const REVERSED_SCORES = [5, 4, 3, 2, 1]

function getCustomersSearch(segmentId: string) {
  return {
    filterBy: { segmentIds: [segmentId] },
    field: "lastMessageSentAt" as const,
    order: "desc" as const,
    page: 1,
  }
}

type SegmentMetrics = z.infer<typeof SegmentGroupShopifyMetricsResponseDataSchema>[number]

type Props = {
  segmentGroup: z.infer<typeof SegmentGroupResponseDataSchema>
  isPending: boolean
}

export function RfmGrid({ segmentGroup, isPending }: Props) {
  const { t } = useTranslation()
  const { getSegmentTranslation } = usePredefinedSegmentTranslations()
  const { data: metrics } = useSegmentGroupShopifyMetricsQuery({ id: segmentGroup.id })

  if (isPending) {
    return (
      <div className="h-65 flex items-center justify-center">
        <Loading />
      </div>
    )
  }

  const segments = segmentGroup.segments ?? []
  const gridSegments = segments.filter((segment) => segment.name in RFM_LAYOUT)
  const otherSegments = segments.filter((segment) => !(segment.name in RFM_LAYOUT))

  const formatShare = (count: number) =>
    formatPercent({ amount: (count / (segmentGroup.customersCount || 1)) * 100, fix: 2 })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row gap-2">
        <div className="flex items-center">
          <span className="[writing-mode:vertical-rl] rotate-180 text-sm text-muted-foreground">
            {t("segments.rfm.frequencyMonetaryScore")}
          </span>
        </div>
        <div className="grid grid-rows-5 gap-1 h-100 text-xs text-muted-foreground">
          {REVERSED_SCORES.map((score) => (
            <span key={score} className="flex items-center">
              {score}
            </span>
          ))}
        </div>
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <div className="grid grid-cols-5 grid-rows-[repeat(5,minmax(56px,1fr))] gap-1 h-[400px]">
            {gridSegments.map((segment) => {
              const layout = ensure(RFM_LAYOUT[segment.name])

              return (
                <Tooltip key={segment.id} delayDuration={100}>
                  <TooltipTrigger asChild>
                    <Link
                      to="/customers/list"
                      search={getCustomersSearch(segment.id)}
                      className={cn(
                        "relative flex items-center justify-center rounded-md p-2 overflow-hidden",
                        "transition-opacity hover:opacity-80",
                        layout.className,
                      )}
                      style={{ gridColumn: layout.column, gridRow: layout.row }}
                    >
                      <span className="absolute top-1.5 left-2 text-xs truncate max-w-[calc(100%-1rem)]">
                        {getSegmentTranslation(segment.name, segmentGroup)}
                      </span>
                      <span className="flex flex-row flex-wrap items-baseline justify-center gap-x-1.5 pt-3">
                        <span className="text-lg font-medium font-mono">
                          {formatNumber({ amount: segment.count, fix: 0 })}
                        </span>
                        <span className="text-xs opacity-80">
                          {t("segments.distribution.percentage", {
                            percentage: formatShare(segment.count),
                          })}
                        </span>
                      </span>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent variant="card" className="p-3 text-left">
                    <RfmSegmentTooltip
                      name={getSegmentTranslation(segment.name, segmentGroup)}
                      segment={segment}
                      share={formatShare(segment.count)}
                      metrics={metrics?.find(({ segmentId }) => segmentId === segment.id)}
                    />
                  </TooltipContent>
                </Tooltip>
              )
            })}
          </div>
          <div className="grid grid-cols-5 text-xs text-muted-foreground text-center">
            {SCORES.map((score) => (
              <span key={score}>{score}</span>
            ))}
          </div>
          <span className="text-sm text-muted-foreground text-center">
            {t("segments.rfm.recencyScore")}
          </span>
        </div>
      </div>
      {otherSegments.length > 0 && (
        <div className="flex flex-row flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
          {otherSegments.map((segment) => (
            <Link
              key={segment.id}
              to="/customers/list"
              search={getCustomersSearch(segment.id)}
              className="hover:text-foreground"
            >
              {getSegmentTranslation(segment.name, segmentGroup)}
              {": "}
              {t("segments.rfm.countWithShare", {
                value: formatNumber({ amount: segment.count, fix: 0 }),
                share: formatShare(segment.count),
              })}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

type RfmSegmentTooltipProps = {
  name: string
  segment: z.infer<typeof SegmentResponseDataSchema>
  share: string
  metrics?: SegmentMetrics
}

function RfmSegmentTooltip({ name, segment, share, metrics }: RfmSegmentTooltipProps) {
  const { t } = useTranslation()

  const rows = [
    {
      label: t("segments.rfm.metrics.customers"),
      value: t("segments.rfm.countWithShare", {
        value: formatNumber({ amount: segment.count, fix: 0 }),
        share,
      }),
    },
    {
      label: t("segments.rfm.metrics.avgDaysSinceLastOrder"),
      value: isNil(metrics?.avgDaysSinceLastOrder)
        ? "-"
        : t("segments.rfm.metrics.days", { count: Math.round(metrics.avgDaysSinceLastOrder) }),
    },
    {
      label: t("segments.rfm.metrics.ordersCount"),
      value: formatNumber({ amount: metrics?.ordersCount, fix: 0 }),
    },
    {
      label: t("segments.rfm.metrics.amountSpent"),
      value: formatAmountSpent(metrics),
    },
  ]

  return (
    <div className="flex flex-col gap-2 min-w-56">
      <span className="text-sm font-medium">{name}</span>
      <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1">
        {rows.map(({ label, value }) => (
          <Fragment key={label}>
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="text-right font-mono">{value}</dd>
          </Fragment>
        ))}
      </dl>
    </div>
  )
}

function formatAmountSpent(metrics?: SegmentMetrics) {
  if (isNil(metrics?.amountSpent)) return "-"

  return new Intl.NumberFormat(undefined, {
    style: metrics.currency ? "currency" : "decimal",
    currency: metrics.currency ?? undefined,
    maximumFractionDigits: 0,
  }).format(metrics.amountSpent)
}
